import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import { CONVERTTIME } from '../../../../utils/dateController';
import MenuIcon from '../../../../materialUi/menuIcon';
import '../../../../css/registrationList.css';

function getArray(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function normalizeID(value) {
  if (!value) return '';
  if (value._id) return value._id.toString();
  return value.toString();
}

function getMembershipData(registration) {
  if (!registration || !registration.membershipData) return {};
  if (Array.isArray(registration.membershipData)) {
    return registration.membershipData[0] || {};
  }
  return registration.membershipData;
}

function getWaitingListData(registration, programWaitingList) {
  const waitingListData = getArray(registration.waitingListData);
  if (waitingListData.length > 0) return waitingListData;

  return programWaitingList.map((waitingListID) => ({
    _id: waitingListID,
    familyMembers: [],
  }));
}

function getWaitlistedFamilyMemberIDs(registration, waitingListData) {
  const familyMemberIDs = waitingListData.reduce((ids, waitingList) => {
    getArray(waitingList.familyMembers).forEach((familyMemberID) => {
      const normalizedID = normalizeID(familyMemberID);
      if (normalizedID) ids.add(normalizedID);
    });
    return ids;
  }, new Set());

  if (familyMemberIDs.size > 0) return Array.from(familyMemberIDs);

  return getArray(registration.familyMembers).map(normalizeID).filter(Boolean);
}

function getWaitlistedFamilyMembers(registration, membershipData, waitingListData) {
  const waitlistedFamilyMemberIDs = getWaitlistedFamilyMemberIDs(
    registration,
    waitingListData,
  );
  const candidates = [
    ...getArray(registration.waitingListFamilyMembersData),
    ...getArray(registration.familyMembersData),
    ...getArray(membershipData.familyMembersInformation),
  ];
  const familyMembersByID = candidates.reduce((familyMembers, familyMember) => {
    const familyMemberID = normalizeID(familyMember);
    if (familyMemberID && !familyMembers[familyMemberID]) {
      familyMembers[familyMemberID] = familyMember;
    }
    return familyMembers;
  }, {});

  if (waitlistedFamilyMemberIDs.length > 0) {
    return waitlistedFamilyMemberIDs.map((familyMemberID) => (
      familyMembersByID[familyMemberID] || { _id: familyMemberID }
    ));
  }

  return candidates;
}

function getWaitingListRow(registration) {
  if (!registration) return null;

  const programWaitingList = getArray(registration.programWaitingList);
  if (programWaitingList.length < 1) return null;

  const membershipData = getMembershipData(registration);
  const waitingListData = getWaitingListData(registration, programWaitingList);
  const familyMembersData = getWaitlistedFamilyMembers(
    registration,
    membershipData,
    waitingListData,
  );
  const displayMember = familyMembersData.find(
    (familyMember) => familyMember.firstName || familyMember.lastName,
  ) || {};

  return {
    membershipID: registration.membershipID || membershipData.membershipID || 'N/A',
    firstName: displayMember.firstName || 'Unknown',
    lastName: displayMember.lastName || 'Member',
    email: membershipData.email || 'N/A',
    phone: displayMember.phone || 'N/A',
    registrationDate: registration.registrationDate
      ? CONVERTTIME(registration.registrationDate, true)
      : 'N/A',
    paymentStatus: registration.paymentStatus || 'Pending',
    id: programWaitingList,
    numberOfAttendees: familyMembersData.length,
    membershipData,
    familyMembersData,
    waitingListData,
    programRegistrationID: registration._id,
  };
}

function rowMatchesSearch(row, sortSearch) {
  if (!sortSearch) return true;
  const searchValue = sortSearch.toLowerCase();
  return [row.membershipID, row.firstName, row.lastName, row.email]
    .some((value) => String(value || '').toLowerCase().includes(searchValue));
}

function rowMatchesStatus(row, sortStatus) {
  if (!Array.isArray(sortStatus) || sortStatus.length < 1) return true;
  return sortStatus.some(
    (status) => String(status || '').toLowerCase()
      === String(row.paymentStatus || '').toLowerCase(),
  );
}

function getWaitingListRows(registrationData, sortStatus, sortSearch) {
  return getArray(registrationData)
    .map(getWaitingListRow)
    .filter((row) => (
      row
      && rowMatchesStatus(row, sortStatus)
      && rowMatchesSearch(row, sortSearch)
    ));
}

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === 'desc' ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
}

const headRows = [
  {
    id: 'position',
    numeric: false,
    disablePadding: true,
    label: '',
  },
  {
    id: 'membershipID',
    numeric: false,
    disablePadding: true,
    label: 'Membership ID',
  },
  {
    id: 'name',
    numeric: false,
    disablePadding: false,
    label: 'Name',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email',
  },
  {
    id: 'phone',
    numeric: false,
    disablePadding: false,
    label: 'Phone',
  },
  {
    id: 'numberOfAttendees',
    numeric: true,
    disablePadding: false,
    label: '# of Attendees',
  },
  {
    id: 'menu',
    numeric: true,
    disablePadding: false,
    label: '',
  },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead className="bg-light">
      <TableRow>
        <TableCell padding="checkbox" className="check" />
        {headRows.map((row) => (
          <TableCell
            key={row.id}
            align={row.numeric ? 'center' : 'left'}
            padding={row.disablePadding ? 'none' : 'default'}
            sortDirection={orderBy === row.id ? order : false}
            className="table-cell"
          >
            <TableSortLabel
              active={orderBy === row.id}
              direction={order}
              onClick={createSortHandler(row.id)}
            >
              {row.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  onRequestSort: PropTypes.func.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const EnhancedTableToolbar = (props) => <div />;

EnhancedTableToolbar.propTypes = {
  numSelected: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3),
  },
  paper: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: 'auto',
  },
}));

export default function WaitingListTable({
  registrationData,
  sortStatus,
  sortSearch,
  handleSendNotification,
}) {
  const rows = getWaitingListRows(registrationData, sortStatus, sortSearch);

  const classes = useStyles();
  const [order, setOrder] = React.useState('asc');
  const [orderBy, setOrderBy] = React.useState('registrationDate');
  const [selected, setSelected] = React.useState([]);
  const [page, setPage] = React.useState(0);
  const [dense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);

  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(property);
  }

  function handleSelectAllClick(event) {
    if (event.target.checked) {
      const newSelected = rows.map((n) => n.name);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
  }

  const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const menuOptions = ['Send payment email', 'Add member(s) to attendance list'];

  return (
    <div className={classes.root}>
      <Paper className={classes.paper} elevation={0}>
        <EnhancedTableToolbar numSelected={selected.length} />
        <div className={classes.tableWrapper}>
          <Table
            className={classes.table}
            aria-labelledby="tableTitle"
            size={dense ? 'small' : 'medium'}
          >
            <EnhancedTableHead
              numSelected={selected.length}
              order={order}
              orderBy={orderBy}
              onSelectAllClick={handleSelectAllClick}
              onRequestSort={handleRequestSort}
              rowCount={rows.length}
            />
            <TableBody>
              {stableSort(rows, getSorting(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow
                      hover
                      role="button"
                      tabIndex={0}
                      className="table-tabSelection"
                      key={row.programRegistrationID || index}
                    >
                      <TableCell padding="checkbox" className="check" />
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                        className="table-cell table-links"
                        role="button"
                      >
                        {row.membershipID}
                      </TableCell>
                      <TableCell align="left" className="text-capitalize">
                        {row.firstName} {row.lastName}
                      </TableCell>
                      <TableCell align="left">{row.email}</TableCell>
                      <TableCell align="left">{row.phone}</TableCell>
                      <TableCell align="center" style={{ paddingLeft: '0px' }}>
                        <div className="registrationList-numberOfAttendees">
                          {row.numberOfAttendees}
                        </div>
                      </TableCell>
                        <TableCell align="right">
                          <MenuIcon
                            list={menuOptions}
                            onClick={(value) => handleSendNotification(value, row)}
                            width="187"
                          />
                        </TableCell>
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && <TableRow style={{ height: 49 * emptyRows }} />}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            'aria-label': 'Previous Page',
          }}
          nextIconButtonProps={{
            'aria-label': 'Next Page',
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}

WaitingListTable.propTypes = {
  sortStatus: PropTypes.arrayOf(PropTypes.string).isRequired,
  sortSearch: PropTypes.string.isRequired,
  registrationData: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      membershipData: PropTypes.arrayOf(
        PropTypes.shape({
          email: PropTypes.string,
          firstName: PropTypes.string,
          lastName: PropTypes.string,
          membershipID: PropTypes.string,
        })
      ),
    }),
  ]).isRequired,
  handleSendNotification: PropTypes.func.isRequired,
};
